import { useState } from 'react';
import { ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { getJobCard } from '@/constants/jobCards';

// Shows the step-by-step card for a division + job. Renders nothing if there is no card.
const JobCardSteps = ({ division, job, defaultOpen = false, className = '' }) => {
  const [open, setOpen] = useState(defaultOpen);
  const card = getJobCard(division, job);
  if (!card) return null;

  let n = 0;
  const rows = card.steps.map((s) => (s.stop ? { ...s } : { ...s, number: ++n }));

  return (
    <div className={className} data-testid="job-card-steps" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1 text-sm font-medium text-blue-700 hover:underline"
        data-testid="job-card-toggle"
      >
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        {open ? 'Hide steps' : 'Show steps'}
      </button>

      {open && (
        <div className="mt-2 rounded-lg border bg-white p-4 text-sm text-slate-700 space-y-4">
          <p className="font-semibold text-slate-900">{card.title}</p>

          {card.before && card.before.length > 0 && (
            <div className="rounded-md border border-teal-700/40 bg-teal-50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-teal-800 mb-1">Before you start you need</p>
              <ul className="list-disc pl-5 space-y-0.5">
                {card.before.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="divide-y">
            {rows.map((row, idx) =>
              row.stop ? (
                <div key={idx} className="my-2 flex items-start gap-2 rounded-md bg-red-700 px-3 py-2 text-white">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider">Stop</p>
                    <p className="font-semibold">{row.stop}</p>
                  </div>
                </div>
              ) : (
                <div key={idx} className="flex flex-wrap items-start gap-x-3 gap-y-1 py-2">
                  <span className="w-5 shrink-0 text-right font-mono text-xs text-slate-400 mt-0.5">{row.number}</span>
                  <p className="flex-1 min-w-[200px]">{row.text}</p>
                  {row.tag && (
                    <span className="text-xs font-semibold text-teal-800 whitespace-nowrap">→ {row.tag}</span>
                  )}
                </div>
              )
            )}
          </div>

          {card.finishedWhen && (
            <div className="rounded-md border-2 border-slate-800 p-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-900 mb-1">You are finished when</p>
              <p>{card.finishedWhen}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default JobCardSteps;
